import { History } from 'history';
import React, { useEffect, useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import { connect } from 'react-redux';
import { useInvoiceOptions } from '../../../../hooks/use-invoice-options';
import { getAccountDisplay, IAccountBillTo } from '../../../../models/account';
import { RootState } from '../../../../store';
import {
    InvoiceOptions,
    WarrantyPeriod,
} from '../../../../models/invoice-options';
import * as accountActions from '../../../../store/account/account-actions';
import * as apiStatusActions from '../../../../store/api/api-status-action';
import FormActions from '../../../common/form-actions';
import PageTitle from '../../../common/page.title';
import SelectInput from '../../../inputs/select-input';
import RepairWarrantyList from './repair-warranty-list';
import { AuthConsumer } from '../../../authorization/auth-context';
import { Buckets } from '../../../../models/buckets-enum';
import parse from 'html-react-parser';

const mapInvoiceOptions = (
    billToAccounts: IAccountBillTo[],
    invoiceOptions: InvoiceOptions[]
): InvoiceOptions[] => {
    const mapped: InvoiceOptions[] = [];

    // eslint-disable-next-line array-callback-return
    billToAccounts.map((b) => {
        // eslint-disable-next-line array-callback-return
        b.shipToAccounts.map((s) => {
            const invoiceOption = invoiceOptions.find(
                (o) =>
                    o.billTo === b.accountNumber && o.shipTo === s.accountNumber
            );
            mapped.push({
                billTo: b.accountNumber,
                shipTo: s.accountNumber,
                shipToAccount: s,
                buyingGroup: invoiceOption ? invoiceOption.buyingGroup : false,
                defaultWarrantyPeriod: invoiceOption
                    ? invoiceOption.defaultWarrantyPeriod
                    : WarrantyPeriod.SixMonths,
            });
        });
    });

    return mapped;
};

interface IRepairWarrantyPreferencesProps extends apiStatusActions.IsBusyProps {
    billToAccounts: IAccountBillTo[];
    history: History;
    loadBillToAccounts: () => Promise<void>;
}
export const RepairWarrantyPreferences = ({
    billToAccounts,
    history,
    loadBillToAccounts,
    setIsBusy,
}: IRepairWarrantyPreferencesProps) => {
    const intl = useIntl();
    const [invoiceOptionsState, setInvoiceOptionsState] = useState<
        InvoiceOptions[]
    >([]);
    const [filteredInvoiceOptions, setFilteredInvoiceOptions] = useState<
        InvoiceOptions[]
    >([]);
    const [selectedBillTo, setSelectedBillTo] = useState('');
    const [invoiceOptions, optionsLoading, invoiceOptionError, saveOptions] =
        useInvoiceOptions();

    useEffect(() => {
        loadBillToAccounts();
    }, []);

    useEffect(() => {
        if (optionsLoading || billToAccounts.length === 0) {
            return;
        }

        setInvoiceOptionsState(
            mapInvoiceOptions(billToAccounts, invoiceOptions)
        );
    }, [optionsLoading, invoiceOptions, billToAccounts]);

    useEffect(() => {
        setFilteredInvoiceOptions(
            invoiceOptionsState.filter((o) => o.billTo === selectedBillTo)
        );
    }, [invoiceOptionsState, selectedBillTo]);

    const handleBillToChange = (event: any) => {
        const { value } = event.target;

        setIsBusy(true, 150);
        setSelectedBillTo(value);
    };

    const handleChange = (event: any, p: InvoiceOptions) => {
        const { name, value } = event.target;

        setInvoiceOptionsState((prevState) =>
            prevState.map((s) => {
                if (s === p) {
                    return { ...s, [name]: +value, isDirty: true };
                }
                return s;
            })
        );
    };

    const handleCopy = (p: InvoiceOptions) => {
        setIsBusy(true);

        setTimeout(() => {
            setInvoiceOptionsState((prevState) =>
                prevState.map((s) => {
                    if (s.billTo === p.billTo && s !== p) {
                        return {
                            ...s,
                            defaultWarrantyPeriod: p.defaultWarrantyPeriod,
                            isDirty: true,
                        };
                    }
                    return s;
                })
            );
            setIsBusy(false);
        }, 150);
    };

    const handleSave = async () => {
        const optionsToSave = invoiceOptionsState.filter((x) => x.isDirty);
        const ok = await saveOptions(optionsToSave);

        if (ok) {
            history.push('/my-account/preferences');
        }
    };

    return (
        <>
            {billToAccounts.length > 0 && (
                <>
                    <PageTitle pageTitleId="Repair_Warranty_Preferences" />
                    <h1 className="underline">
                        <FormattedMessage id="Repair_Warranty_Preferences" />
                    </h1>
                    <>
                        <div className="well">
                            <div className="row form-horizontal">
                                <SelectInput
                                    name="billTo"
                                    label={'Bill To Account'}
                                    options={billToAccounts.map((b) => ({
                                        value: b.accountNumber,
                                        text: getAccountDisplay(b),
                                    }))}
                                    value={selectedBillTo}
                                    hasDefaultOption={true}
                                    defaultOption={'Select'}
                                    labelClassName="col-sm-2"
                                    onChange={handleBillToChange}
                                />
                            </div>
                        </div>
                        {filteredInvoiceOptions.length > 0 && (
                            <AuthConsumer>
                                {({ buckets }) => {
                                    const isReadOnly = !buckets.find(
                                        (x) => x === Buckets.Preferences
                                    );
                                    return (
                                        <>
                                            <RepairWarrantyList
                                                invoiceOptions={
                                                    filteredInvoiceOptions
                                                }
                                                isReadOnly={isReadOnly}
                                                onChange={handleChange}
                                                onCopy={handleCopy}
                                            />
                                            {invoiceOptionError && (
                                                <div className="alert alert-danger gutter-top">
                                                    <span
                                                        style={{
                                                            whiteSpace:
                                                                'pre-wrap',
                                                        }}
                                                    >
                                                        {parse(
                                                            invoiceOptionError
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                            <FormActions
                                                cancelUrl="/my-account/preferences"
                                                hideSave={
                                                    isReadOnly ||
                                                    filteredInvoiceOptions.every(
                                                        (x) => x.buyingGroup
                                                    )
                                                }
                                                processingText={intl.formatMessage(
                                                    {
                                                        id: 'One_Business_Day_Processing',
                                                    }
                                                )}
                                                pullRight={true}
                                                handleSave={handleSave}
                                            />
                                        </>
                                    );
                                }}
                            </AuthConsumer>
                        )}
                    </>
                </>
            )}
        </>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        billToAccounts: state.billToAccounts ? state.billToAccounts.data! : [],
    };
};

const mapDispatchToProps = {
    loadBillToAccounts: accountActions.loadBillToAccounts,
    setIsBusy: apiStatusActions.setIsBusy,
};

const repairWarrantyContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(RepairWarrantyPreferences);

export default repairWarrantyContainer;
